import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import type { ListSort } from '@/services/core/paging'
import { BoolCell, DateCell, ListCell, NameCell, NumberCell } from '@/components/TableCells'
import { Layout } from '@/services/sites/layoutsApi'

/** The API field each sortable column orders by when the server sorts the list. */
const SORT_FIELDS: Record<string, string> = {
  name: 'name',
  layoutType: 'layoutType',
  objectState: 'objectState',
  political: 'political',
  fullPage: 'fullPage',
  verifiedSafe: 'verifiedSafe',
  updateTime: 'updateTime',
  createTime: 'createTime',
}

interface LayoutTableProps {
  layouts: Layout[]
  loading?: boolean
  emptyMessage?: string
  selection?: Layout[]
  onSelectionChange?: (layouts: Layout[]) => void
  /** Sorts on the server — pass both for a list the server pages. */
  sort?: ListSort | null
  onSortChange?: (sort: ListSort | null) => void
}

export function LayoutTable({
  layouts,
  loading,
  emptyMessage,
  selection,
  onSelectionChange,
  sort,
  onSortChange,
}: LayoutTableProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const selectable = !!onSelectionChange

  const columns: ColumnDef<Layout, unknown>[] = [
    ...(selectable ? [selectionColumn<Layout>()] : []),
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 200 },
      cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
    },
    {
      accessorKey: 'description',
      header: t('common.description'),
      enableSorting: false,
      meta: { minWidth: 200 },
      cell: ({ row }) => (
        <span style={{ fontSize: 12.5, color: 'var(--ad-text-muted)' }}>
          {row.original.description || '\u2014'}
        </span>
      ),
    },
    {
      accessorKey: 'type',
      header: t('common.type'),
      size: 130,
      cell: ({ row }) => (
        <span style={{ fontSize: 12.5, color: 'var(--ad-text-muted)' }}>
          {row.original.type}
        </span>
      ),
    },
    {
      accessorKey: 'layoutType',
      header: t('layouts.layoutType'),
      size: 130,
      cell: ({ row }) => (
        <span style={{ fontSize: 12.5, color: 'var(--ad-text-muted)' }}>
          {row.original.layoutType}
        </span>
      ),
    },
    {
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      cell: ({ row }) => (
        <span
          className={`chip chip--${row.original.objectState === 'ACTIVE' ? 'running' : 'draft'}`}
          style={{ fontSize: 11 }}
        >
          <span className="dot" />
          {row.original.objectState}
        </span>
      ),
    },
    optionalColumn<Layout>({
      accessorKey: 'political',
      header: t('layouts.political'),
      size: 110,
      cell: ({ row }) => <BoolCell value={row.original.political} />,
    }),
    optionalColumn<Layout>({
      accessorKey: 'fullPage',
      header: t('layouts.fullPage'),
      size: 110,
      cell: ({ row }) => <BoolCell value={row.original.fullPage} />,
    }),
    optionalColumn<Layout>({
      accessorKey: 'verifiedSafe',
      header: t('layouts.verifiedSafe'),
      size: 120,
      cell: ({ row }) => <BoolCell value={row.original.verifiedSafe} />,
    }),
    optionalColumn<Layout>({
      id: 'componentCount',
      header: t('layouts.components'),
      accessorFn: (row) => row.layoutComponents?.length ?? 0,
      size: 120,
      cell: ({ row }) => <NumberCell value={row.original.layoutComponents?.length ?? 0} />,
    }),
    optionalColumn<Layout>({
      id: 'teams',
      header: t('common.teams'),
      enableSorting: false,
      meta: { minWidth: 150 },
      cell: ({ row }) => <ListCell values={row.original.teams?.map((team) => team.name)} />,
    }),
    optionalColumn<Layout>({
      id: 'labels',
      header: t('common.labels'),
      enableSorting: false,
      meta: { minWidth: 150 },
      cell: ({ row }) => <ListCell values={row.original.labels} />,
    }),
    optionalColumn<Layout>({
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    }),
    optionalColumn<Layout>({
      accessorKey: 'createTime',
      header: t('common.created'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.createTime} />,
    }),
  ]

  return (
    <DataTable
      columns={columns}
      data={layouts}
      loading={loading}
      emptyMessage={emptyMessage ?? 'No layouts found.'}
      columnsKey="layouts"
      onRowClick={(row) => navigate(`/admin/layouts/${row.id}`)}
      {...(selectable ? { selection, onSelectionChange } : {})}
      serverSort={
        onSortChange && { sort: sort ?? null, onChange: onSortChange, fields: SORT_FIELDS }
      }
    />
  )
}
